import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, map } from 'rxjs';
import { USER_QUERIES } from '@/app/platform/platform-core/user/store/types';
import { hasFeature } from '@/app/support/support-access.service';
import { HdChartSettingsStore } from '../chart/bodygraph/chart-tabs/hd-chart-settings.store';
import {
  COMMUNITY_CONTENT_PACKS,
  CommunityContentPackId,
} from './community-content.metadata';

/**
 * Toegang tot community-content: een pack is pas zichtbaar wanneer de
 * gebruiker én de UPAM-feature van dat pack heeft (toegekend per
 * gebruikersgroep) én community-content in de applicatie-instellingen heeft
 * aangezet (opt-in, default uit — het gaat om content van communityleden die
 * geen deel uitmaakt van de standaard applicatie).
 */
@Injectable({ providedIn: 'root' })
export class CommunityContentAccessService {
  // Optioneel: in component-specs zonder user-store is er simpelweg geen
  // toegang, in plaats van een injectiefout bij elke `hd-gate-line`.
  private readonly userQueries = inject(USER_QUERIES, { optional: true });
  private readonly settings = inject(HdChartSettingsStore);

  private readonly grantedByPack: Record<CommunityContentPackId, Signal<boolean>> = {
    'line-companion': this.featureGranted(COMMUNITY_CONTENT_PACKS['line-companion'].featureId),
    'iching-wilhelm-nl': this.featureGranted(COMMUNITY_CONTENT_PACKS['iching-wilhelm-nl'].featureId),
  };

  /** Feature + opt-in-setting voor een pack. */
  packVisible(pack: CommunityContentPackId): boolean {
    return this.grantedByPack[pack]() && this.settings.communityContentEnabled();
  }

  /** Line Companion zichtbaar? (feature `EnableCommunityLineContent` + setting) */
  readonly lineCompanionVisible = computed(() => this.packVisible('line-companion'));

  /**
   * Nederlandse Wilhelm-tekst (pack-feature) toegekend? Bewust zonder
   * `communityContentEnabled`: Classic I Ching heeft zijn eigen setting
   * (zie HdIChingAccessService).
   */
  readonly wilhelmNlGranted = computed(() => this.grantedByPack['iching-wilhelm-nl']());

  private featureGranted(featureId: string): Signal<boolean> {
    if (this.userQueries == null) {
      return computed(() => false);
    }
    return toSignal(
      combineLatest([this.userQueries.isAnyUserAuthenticated$, this.userQueries.availableApps$]).pipe(
        map(([authenticated, apps]) => authenticated && hasFeature(apps, featureId)),
      ),
      { initialValue: false },
    );
  }
}
